import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ChevronRight, User, Power, LayoutDashboard, Home, Moon, Sun, Shield, Edit2, FileText, Layout, BookOpen, Users, Settings, FileEdit, Activity, LogOut, Building2, Factory, HelpCircle, PhoneCall } from 'lucide-react';
import authService from '../../../services/auth.service';
import modulesService from '../../../services/modules.service';
import apiClient, { API_BASE_URL } from '../../../utils/apiClient';
import Sidebar from '../../Sidebar/Sidebar';
import HelpDrawer from '../../Help/HelpDrawer';
import PageTransition from '../../Common/PageTransition';
import './AppLayout.css';
import { jwtDecode } from "jwt-decode";


const AppLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLogoutTray, setShowLogoutTray] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [userRoleCount, setUserRoleCount] = useState(0);
    const token = authService.getToken();
    const payload: any = token ? jwtDecode(token) : null;

    const isTempSession = payload?.temp === true;

    const [hasModuleAccess, setHasModuleAccess] = useState(true);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved === 'dark';
    });
    const logoutRef = useRef<HTMLDivElement>(null);

    const getBreadcrumbs = () => {
        const path = location.pathname;
        const crumbs: { icon: any, label: string }[] = [];

        crumbs.push({ icon: Home, label: '' });

        if (path === '/dashboard' || path === '/') {
            if (hasModuleAccess) {
                crumbs.push({ icon: LayoutDashboard, label: 'Dashboard' });
            }
            return crumbs;
        }

        if (path === '/profile') {
            crumbs.push({ icon: User, label: 'Profile' });
            return crumbs;
        }

        if (path === '/users-permissions') {
            crumbs.push({ icon: Shield, label: 'User & Role Permission' });
            return crumbs;
        }

        if (path.startsWith('/documents')) {
            crumbs.push({ icon: FileText, label: 'Documents' });
            if (path.includes('/upload')) crumbs.push({ icon: FileText, label: 'Upload' });
            else if (path !== '/documents') crumbs.push({ icon: FileText, label: 'Details' });
            return crumbs;
        }

        if (path.startsWith('/templates')) {
            crumbs.push({ icon: Layout, label: 'Templates' });
            if (path.includes('/create')) crumbs.push({ icon: Layout, label: 'Create' });
            else if (path.includes('/edit')) crumbs.push({ icon: Layout, label: 'Edit' });
            return crumbs;
        }

        if (path.startsWith('/sops')) {
            crumbs.push({ icon: BookOpen, label: 'SOPs' });
            if (path.includes('/create')) crumbs.push({ icon: BookOpen, label: 'Create' });
            else if (path.includes('/edit/{id}')) crumbs.push({ icon: BookOpen, label: 'Edit' });
            return crumbs;
        }

        if (path.startsWith('/clients')) {
            crumbs.push({ icon: Users, label: 'Clients' });
            return crumbs;
        }

        if (path.startsWith('/organisations')) {
            crumbs.push({ icon: Building2, label: 'Organisations' });
            return crumbs;
        }

        if (path.startsWith('/settings')) {
            crumbs.push({ icon: Settings, label: 'Settings' });
            return crumbs;
        }

        if (path.startsWith('/forms')) {
            crumbs.push({ icon: FileEdit, label: 'Forms' });
            if (path.includes('/create')) crumbs.push({ icon: FileEdit, label: 'Create' });
            else if (path !== '/forms') crumbs.push({ icon: FileEdit, label: 'Edit' });
            return crumbs;
        }

        if (path.startsWith('/activity-logs')) {
            crumbs.push({ icon: Activity, label: 'Activity Logs' });
            return crumbs;
        }

        if (path.startsWith('/patient-calling')) {
            crumbs.push({ icon: PhoneCall, label: 'Patient Calling' });
            return crumbs;
        }

        if (hasModuleAccess) {
            crumbs.push({ icon: LayoutDashboard, label: 'Dashboard' });
        }
        return crumbs;
    };

    const formatRoleName = (name: string) => {
        if (!name) return '';
        const formatted = name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');

        return formatted === 'Organisation Role' ? 'Organisation' : formatted;
    };

    useEffect(() => {
        const currentUser = authService.getUser();
        setUser(currentUser);

        if (currentUser?.email) {
            fetchUserRoleCount();
            checkModuleAccess(currentUser.email);
        }

        const handleAuthChange = () => {
            const updatedUser = authService.getUser();
            setUser(updatedUser);
        };
        authService.subscribe(handleAuthChange);

        // Apply saved theme on mount
        document.documentElement.classList.toggle('dark', isDarkMode);

        return () => {
            authService.unsubscribe(handleAuthChange);
        };
    }, [isDarkMode]);

    const checkModuleAccess = async (email: string) => {
        try {
            const modules = await modulesService.getUserModules(email);
            setHasModuleAccess(modules.length > 0);
        } catch (error) {
            console.error('Failed to check module access', error);
        }
    };
    const handleSwitchOrganisation = async () => {
        try {
            await authService.exitOrganisation();
            navigate('/organisations');
        } catch (err) {
            console.error(err);
        }
    };
    const fetchUserRoleCount = async () => {
        try {
            const response = await apiClient(`${API_BASE_URL}/api/users/me`);
            if (response.ok) {
                const userData = await response.json();
                setUserRoleCount(userData.roles?.length || 0);

                // Sync with auth service to ensure profile_image_url is updated locally
                const currentUser = authService.getUser();
                if (currentUser) {
                    authService.saveUser({
                        ...currentUser,
                        ...userData
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch user roles:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (logoutRef.current && !logoutRef.current.contains(event.target as Node)) {
                setShowLogoutTray(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDarkMode = () => {
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        document.documentElement.classList.toggle('dark', newDarkMode);
        localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    };

    // const handleLogout = () => {
    //     authService.logout();
    //     sessionStorage.removeItem('hasSeenIntro');
    //     navigate('/login');
    // };
    const handleLogout = () => {
        authService.logout();
        sessionStorage.removeItem('hasSeenIntro');
        window.location.href = "/login";   // HARD RESET
    };
    const hidesidebar = location.pathname.startsWith('/organisations') ||
        location.pathname.startsWith('/sops/create') ||
        location.pathname.startsWith('/sops/edit');
    return (
        <div className="app-layout">
            {/* <Sidebar /> */}
            {!hidesidebar && <Sidebar />}
            <div className={`app-content ${(isTempSession || hidesidebar) ? 'no-sidebar' : ''}`}>
                <header className="app-header">
                    <div className="breadcrumb">
                        {getBreadcrumbs().map((crumb, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <ChevronRight size={14} />}
                                <crumb.icon size={14} />
                                {crumb.label && <span>{crumb.label}</span>}
                            </React.Fragment>
                        ))}
                        <div id="breadcrumb-extra" className="breadcrumb-extra"></div>
                    </div>
                    <div className="user-info">
                        {/* <button className="theme-toggle" onClick={toggleDarkMode}>
                            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                        </button> */}
                        {!location.pathname.startsWith('/organisations') && user?.organisation_name && (
                            <>
                                <div className={`organisation-name-pill ${!user?.is_superuser ? 'no-edit-btn' : ''}`}>
                                    <Factory size={16} />
                                    <span>{user.organisation_name}</span>
                                    {user?.is_superuser && (
                                        <button
                                            onClick={handleSwitchOrganisation}
                                            className="switch-org-btn"
                                            title="Switch Organisation"
                                        >
                                            <Edit2 size={13} />
                                        </button>
                                    )}
                                </div>
                                <div className="h-divider" />
                            </>
                        )}
                        {user?.role && (
                            <>
                                <div className="role-display">
                                    <Shield size={16} />
                                    <span>{formatRoleName(user.role.name)}</span>
                                    {userRoleCount > 1 && (
                                        <button
                                            className="role-edit-btn"
                                            onClick={() => {
                                                navigate('/login', { state: { showRoleSelection: true } });
                                            }}
                                            title="Change role"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                    )}
                                </div>
                                <div className="h-divider" />
                            </>
                        )}
                        {!user?.role && payload?.superadmin && (
                            <>
                                <div className="role-display">
                                    <Shield size={16} />
                                    <span>Super Admin</span>
                                </div>
                                <div className="h-divider" />
                            </>
                        )}
                        <div className="user-details" onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
                            <div className="user-avatar" style={user?.profile_image_url ? { backgroundImage: `url("${user.profile_image_url}")`, backgroundSize: '130%', backgroundPosition: 'center' } : {}}>
                                {!user?.profile_image_url && (
                                    (user?.username || user?.first_name)
                                        ? (user?.username || user?.first_name).charAt(0).toUpperCase()
                                        : <User size={16} />
                                )}                            </div>
                            <span>{user?.username || user?.first_name || user?.email || 'User'}</span>
                        </div>
                        <div className="h-divider" />
                        <button className="help-btn-header" onClick={() => setHelpOpen(true)} title="Help & Support">
                            <HelpCircle size={16} />
                        </button>
                        <div className="h-divider" />
                        <div className="logout-container" ref={logoutRef}>
                            <button
                                className="logout-btn"
                                onClick={() => setShowLogoutTray(!showLogoutTray)}
                            >
                                <Power size={16} />
                            </button>
                            {showLogoutTray && (
                                <div className="logout-tray">
                                    <button className="logout-option" onClick={handleLogout}>
                                        <LogOut size={16} />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <main className="app-main">
                    <AnimatePresence mode="wait">
                        <PageTransition key={location.pathname}>
                            <Outlet />
                        </PageTransition>
                    </AnimatePresence>
                </main>
                <div id="pagination-target"></div>
            </div>
            <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
    );
};

export default AppLayout;